import { ContactFormData, ContractorContactFormProps, ContactTypeCode } from "./types";
import { ContractorData, getHiddenFieldsByContactType, getRequiredFieldsByContactType, ContactType } from "components/utils/contacts";
import { RequireContact } from "types/RequireContact";
import { getContactName, getReqFields, needsFirstAndLastOrCompany } from "./utils";
import { useCustomerContacts } from "components/utils/useCustomerContacts";
import { isEmpty } from "lodash";
import cn from "classnames";
import ContactFields from "./ContactFields";
import { ExistingContact } from "./ExistingContact";
import { Form } from "react-bootstrap";
import { ContractorSearch } from "components/ContractorSearch";
import { useLotLabel } from "./useLotLabel";

export const ContractorContactForm = ({
    formData,
    errors,
    requirements,
    applicationNumber,
    onUseCustomerContact,
    onChange,
    onClearForm,
}: ContractorContactFormProps) => {
    const [customerContacts = []] = useCustomerContacts(1, 100);
    const requiredFields = getRequiredFieldsByContactType(requirements.requiredFields, ContactType.Contractor);
    const lotLabel = useLotLabel(ContactTypeCode.Contractor);
    // first name, last name, company
    const requiredFLC = getReqFields(requiredFields, formData);
    const hiddenFields = getHiddenFieldsByContactType(requirements.hiddenFields, ContactType.Contractor);
    const hideContractor =
        requirements.requireContractor === RequireContact.Hidden ||
        (requirements.requireContractor === RequireContact.ReadOnly && !(formData?.company || formData?.firstName || formData?.lastName));
    if (hideContractor) {
        return null;
    }

    const isContractorRequiredFromList = requirements.requireContractor === RequireContact.RequiredFromList;
    const showContractorSearch = [RequireContact.RequiredFromList, RequireContact.NotRequiredFromList].includes(
        requirements.requireContractor!
    );

    const selectContractor = (contractor: ContractorData) =>
        onChange({
            contactTitle: contractor.contactTitle,
            company: contractor.company ?? undefined,
            firstName: contractor.firstName ?? undefined,
            lastName: contractor.lastName ?? undefined,
            premiseId: contractor.premiseId ?? undefined,
            meterId: contractor.meterId ?? undefined,
            acct_number: contractor.acct_number ?? undefined,
            technologies: contractor.technologies ?? undefined,
            services: contractor.services ?? undefined,
            lot: contractor.lot ?? undefined,
            address: contractor.address ?? undefined,
            address_cont: contractor.address_cont ?? undefined,
            city: contractor.city ?? undefined,
            state: contractor.state ?? undefined,
            zip: contractor.zip ?? undefined,
            phone: contractor.phone ?? undefined,
            cell: contractor.cell ?? undefined,
            fax: contractor.fax ?? undefined,
            email: contractor.email ?? undefined,
            taxId: contractor.taxId ?? undefined,
            contactNumber: contractor.contactNumber ?? undefined,
            origId: contractor.origId ?? undefined,
            contactType: ContactTypeCode.Contractor,
        });
    const isFromStoredContact = !!customerContacts.some((c) => c.contactNumber === formData?.storedContactNumber);
    const isReadOnlyContractor = requirements.requireContractor === RequireContact.ReadOnly;

    const isExisting = isFromStoredContact || (showContractorSearch && !!formData?.contactNumber) || isReadOnlyContractor;

    const storedContactNeedsEditing = isFromStoredContact && !isEmpty(errors);

    const showAsExisting = isExisting && !storedContactNeedsEditing;

    const showSearchOnly = showContractorSearch && isEmpty(formData);

    return (
        <div style={{ maxWidth: "925px" }} className="border bg-white mb-3 rounded-8 mx-auto">
            <div className="m-3">
                <h6 className="fs-6 fw-bold mb-3">
                    {requirements.contractorDescription || "Who is installing or completing the work/installation?"}
                </h6>
                <div className="d-flex align-items-center justify-content-between">
                    {!showContractorSearch && customerContacts.length > 0 && (
                        <Form.Group className="w-100" style={{ maxWidth: "320px" }} controlId="storedContact">
                            <Form.Label>Choose from existing contact</Form.Label>
                            <Form.Select
                                value={formData?.storedContactNumber ?? ""}
                                onChange={(e) => {
                                    onUseCustomerContact?.(customerContacts.find((c) => c.contactNumber === e.target.value));
                                }}
                            >
                                <option disabled value={""}>
                                    Select a contact...
                                </option>
                                {customerContacts.map((c) => (
                                    <option key={c.contactNumber} value={c.contactNumber}>
                                        {getContactName(c)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                    <div className={cn("d-flex flex-column", { "field-error": Boolean(errors?.contractorSearch) })}>
                        <ContractorSearch
                            applicationNumber={applicationNumber}
                            hide={!showContractorSearch}
                            onSelect={selectContractor}
                            isContractorRequired={isContractorRequiredFromList}
                            ariaDescribedBy={errors?.contractorSearch ? "contractorSearch-error" : undefined}
                        />
                        {errors?.contractorSearch && (
                            <div className="invalid-feedback d-block" id="contractorSearch-error">
                                {errors.contractorSearch}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!showSearchOnly &&
                (showAsExisting ? (
                    <ExistingContact
                        readonly={isReadOnlyContractor}
                        onClear={onClearForm}
                        onUseCustomerContact={onUseCustomerContact}
                        customerContacts={customerContacts}
                        contact={formData}
                        hiddenFields={hiddenFields}
                        lotLabel={lotLabel}
                    />
                ) : (
                    <ContactFields
                        onChange={(patch: Partial<ContactFormData>) =>
                            onChange({
                                ...formData,
                                ...patch,
                            })
                        }
                        idPrefix="contractor-contact"
                        formData={formData}
                        requiredFields={[
                            ...requiredFields.filter((s) => !["firstname", "lastname", "company"].includes(s)),
                            ...requiredFLC,
                        ]}
                        hiddenFields={hiddenFields}
                        errors={errors}
                        onClearForm={onClearForm}
                        lotLabel={lotLabel}
                        needsFirstAndLastOrCompany={needsFirstAndLastOrCompany(requiredFields)}
                    />
                ))}
        </div>
    );
};
